/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
      /** Background MP4 (`data-background="true"`): defer `video-section--played` until real playback so poster/cover stays. MWT column uses `data-background="false"` and does not use this path. */
      this._bgCoverRevealed = false;
    }

    connectedCallback() {
      this.init();
    }

    /**
     * Theme video root (hero / video section) or MWT 2026 scoped root — avoids sharing `.video-section` with Background video.
     */
    closestVideoArea() {
      return this.closest('.video-section') || this.closest('.mwt-2026__video-root');
    }

    /**
     * Background `<video>`: reveal poster only after real playback (or time fallback), restore on error.
     */
    setupBackgroundTagCoverReveal() {
      const player = this.player;
      const reveal = () => {
        if (this._bgCoverRevealed) return;
        this._bgCoverRevealed = true;
        this._teardownBgCoverFallbacks();
        const area = this.closestVideoArea();
        if (area) area.classList.add('video-section--played');
      };

      const onTimeUpdate = () => {
        if (player.currentTime > 0.08) reveal();
      };

      this._bgCoverFallbackTimer = window.setTimeout(() => {
        this._teardownBgCoverFallbacks();
      }, 12000);

      player.addEventListener('playing', reveal, { once: true });
      player.addEventListener('timeupdate', onTimeUpdate);
      this._bgCoverTimeUpdateFn = onTimeUpdate;

      player.addEventListener('error', () => {
        this._bgCoverRevealed = false;
        this._teardownBgCoverFallbacks();
        const area = this.closestVideoArea();
        if (area) area.classList.remove('video-section--played');
      });
    }

    _teardownBgCoverFallbacks() {
      if (this._bgCoverTimeUpdateFn && this.player) {
        this.player.removeEventListener('timeupdate', this._bgCoverTimeUpdateFn);
        this._bgCoverTimeUpdateFn = null;
      }
      if (this._bgCoverFallbackTimer) {
        clearTimeout(this._bgCoverFallbackTimer);
        this._bgCoverFallbackTimer = null;
      }
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      if (this.background) {
        const area = this.closestVideoArea();
        if (area) area.addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        if (this.background) {
          this.player.on('playing', () => {
            const area = this.closestVideoArea();
            if (area) area.classList.add('video-section--played');
          });
          this.player.on('error', () => {
            const area = this.closestVideoArea();
            if (area) area.classList.remove('video-section--played');
          });
        } else {
          this.player.on('play', () => {
            const area = this.closestVideoArea();
            if (area) area.classList.add('video-section--played');
          });
        }

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      const addPlayedClass = () => {
        const area = this.closestVideoArea();
        if (area) area.classList.add('video-section--played');
      };

      if (this.background) {
        this.setupBackgroundTagCoverReveal();

        if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
          && this.player.readyState > 2) {
          this._bgCoverRevealed = true;
          this._teardownBgCoverFallbacks();
          addPlayedClass();
        } else {
          this.addObserver(this.player);

          const deferredMedia = this.closest('deferred-media');
          if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
            this.player.muted = true;
          }

          this.play();
        }
      } else if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        addPlayedClass();
      } else {
        this.player.addEventListener('play', addPlayedClass, { once: true });

        this.addObserver(this.player);

        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        const area = this.closestVideoArea();
        if (area) area.classList.add('video-section--played');
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        this.player.play();
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
